/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import '../styles/components.css';
import { useAuth } from '../contexts/AuthContext';

const CompanyFirstRun: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    setup_type: 'group' as 'single' | 'group',
    cin: '',
    pan: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    csr_contact: {
      name: '',
      email: '',
      phone: ''
    },
    logo: null as File | null,
    documents: {
      incorporation: null as File | null,
      pan_card: null as File | null,
      gst: null as File | null
    }
  });
  const [existingDocuments, setExistingDocuments] = useState<any>({});
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileChange = (docType: string, file: File | null) => {
    setFormData(prev => ({
      ...prev,
      documents: { ...prev.documents, [docType]: file }
    }));
  };

  const uploadDocument = async (file: File, docType: string, companyId: string): Promise<string> => {
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${companyId}/${docType}-${Date.now()}-${safeName}`;
    const { data, error } = await supabase.storage
      .from('company-documents')
      .upload(fileName, file);

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('company-documents')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  // Load existing company data on mount
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        // Check if user has a company
        const { data: companyLink } = await supabase
          .from('company_users')
          .select('company_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (companyLink?.company_id) {
          // Load company data
          const { data: company, error: companyError } = await supabase
            .from('companies')
            .select('*')
            .eq('id', companyLink.company_id)
            .single();

          if (company && !companyError) {
            // If profile is complete, redirect to dashboard
            if (company.profile_complete) {
              navigate(`/client/company/dashboard`, { replace: true });
              return;
            }

            // Load existing data into form
            setFormData({
              name: company.name || '',
              cin: company.cin || '',
              pan: company.pan || '',
              address: company.address || {
                street: '',
                city: '',
                state: '',
                pincode: '',
                country: 'India'
              },
              csr_contact: company.csr_contact || {
                name: '',
                email: '',
                phone: ''
              },
              logo: null,
              setup_type: company.setup_type || 'group',
              documents: {
                incorporation: null,
                pan_card: null,
                gst: null
              }
            });

            // Load existing documents
            const { data: docs, error: docsError } = await supabase
              .from('company_documents')
              .select('*')
              .eq('company_id', companyLink.company_id);

            if (!docsError && docs) {
              const docsMap: any = {};
              docs.forEach((doc: any) => {
                if (doc.document_type) {
                  docsMap[doc.document_type] = doc.file_url;
                }
              });
              setExistingDocuments(docsMap);
            }

            if (company.logo_url) {
              setLogoPreviewUrl(company.logo_url);
            }
          }
        }
      } catch (err) {
        console.error('Error loading company data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCompanyData();
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: { ...prev.address, [field]: value }
      }));
    } else if (name.startsWith('csr_contact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        csr_contact: { ...prev.csr_contact, [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: companyLink } = await supabase
        .from('company_users')
        .select('company_id')
        .eq('user_id', user.id)
        .maybeSingle();

      let companyId: string;

      if (companyLink?.company_id) {
        companyId = companyLink.company_id;

        // Get current company data to check if CIN/PAN is changing
        const { data: currentCompany } = await supabase
          .from('companies')
          .select('cin, pan')
          .eq('id', companyId)
          .single();

        // Check conflicts explicitly before update to give fast feedback
        if (currentCompany && formData.cin !== currentCompany.cin) {
          const { data: existingCompany } = await supabase
            .from('companies')
            .select('id')
            .eq('cin', formData.cin)
            .neq('id', companyId)
            .maybeSingle();

          if (existingCompany) {
            throw new Error('CIN already exists for another company. Please use a different CIN.');
          }
        }

        if (currentCompany && formData.pan !== currentCompany.pan) {
          const { data: existingCompany } = await supabase
            .from('companies')
            .select('id')
            .eq('pan', formData.pan)
            .neq('id', companyId)
            .maybeSingle();

          if (existingCompany) {
            throw new Error('PAN already exists for another company. Please use a different PAN.');
          }
        }

        let logoUrl = null;
        if (formData.logo) {
          logoUrl = await uploadDocument(formData.logo, 'logo', companyId);
        }

        const updatePayload: any = {
          name: formData.name,
          setup_type: formData.setup_type,
          cin: formData.cin,
          pan: formData.pan,
          address: formData.address,
          csr_contact: formData.csr_contact,
          profile_complete: true,
          first_run_date: new Date().toISOString()
        };
        if (logoUrl) updatePayload.logo_url = logoUrl;

        const { error: companyError } = await supabase
          .from('companies')
          .update(updatePayload)
          .eq('id', companyId);

        if (companyError) throw companyError;

      } else {

        const insertPayload: any = {
          name: formData.name,
          setup_type: formData.setup_type,
          cin: formData.cin,
          pan: formData.pan,
          address: formData.address,
          csr_contact: formData.csr_contact,
          profile_complete: true,
          first_run_date: new Date().toISOString()
        };
        // Create new company
        const { data: newCompany, error: createError } = await supabase
          .from('companies')
          .insert(insertPayload)
          .select()
          .single();

        if (createError) throw createError;
        if (!newCompany) throw new Error('Failed to create company');

        companyId = newCompany.id;

        const { error: linkInsertError } = await supabase
          .from('company_users')
          .insert({
            user_id: user.id,
            company_id: companyId
          });

        if (linkInsertError) {
          await supabase.from('companies').delete().eq('id', companyId);
          throw linkInsertError;
        }

        if (formData.logo) {
          const logoUrl = await uploadDocument(formData.logo, 'logo', companyId);
          await supabase.from('companies').update({ logo_url: logoUrl }).eq('id', companyId);
        }

      }

      // Upload documents
      for (const [docType, file] of Object.entries(formData.documents)) {
        if (file) {
          try {
            const url = await uploadDocument(file, docType, companyId);

            // Insert into company_documents
            await supabase.from('company_documents').insert({
              company_id: companyId,
              document_type: docType,
              file_url: url,
              file_name: file.name
            });
          } catch (docError) {
            console.error(`Failed to upload ${docType}:`, docError);
            // Continue with other docs even if one fails
          }
        }
      }

      // Update profile role - safeguard existing roles
      const { data: currentProfile } = await supabase.from('profiles').select('role').eq('id', user.id).single();

      if (!currentProfile?.role || currentProfile.role?.toLowerCase() === 'company user') {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: user.id,
            role: 'Company User',
            email: user.email
          }, { onConflict: 'id' });

        if (profileError) throw profileError;
      } else {
        console.warn(`[CompanyFirstRun] Not overwriting existing role: ${currentProfile.role}`);
      }

      window.location.href = `/client/company/ngos/invite`;
    } catch (err: any) {
      console.error('Error saving company profile:', err);

      // Better error handling for Unique Constraints
      if (err.code === '23505' || (err.message && err.message.includes('unique constraint'))) {
        if (err.message.includes('companies_cin_key') || err.message.includes('cin')) {
          setError('A company with this CIN already exists. Please check your details.');
        } else if (err.message.includes('companies_pan_key') || err.message.includes('pan')) {
          setError('A company with this PAN already exists. Please check your details.');
        } else {
          setError('This company is already registered.');
        }
      } else {
        setError(err.message || 'Failed to save company profile');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div className="modal-content" style={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        position: 'relative'
      }}>
        <div className="form-container" style={{ padding: '2rem' }}>
          <div className="form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h1 style={{ margin: 0 }}>Complete Company Profile</h1>
              <button
                type="button"
                onClick={async () => {
                  await supabase.auth.signOut();
                  navigate('/login');
                }}
                className="btn btn-sm btn-outline"
                style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
              >
                Sign Out
              </button>
            </div>

            <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
              Please provide your company details to get started
            </p>

            {error && (
              <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', marginBottom: '2rem' }}>
                <div
                  onClick={() => logoFileInputRef.current?.click()}
                  style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    border: '2px dashed #d1d5db',
                    position: 'relative'
                  }}
                >
                  {logoPreviewUrl ? (
                    <img src={logoPreviewUrl} alt="Logo Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                      <div style={{ fontSize: '24px' }}>📷</div>
                      <div style={{ fontSize: '12px' }}>Logo</div>
                    </div>
                  )}
                  <input
                    type="file"
                    ref={logoFileInputRef}
                    onChange={handleLogoChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Company Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter company name"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '1rem', fontWeight: '600' }}>Setup Mode *</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div
                    onClick={() => setFormData(prev => ({ ...prev, setup_type: 'single' }))}
                    style={{
                      padding: '1.5rem',
                      border: `2px solid ${formData.setup_type === 'single' ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.setup_type === 'single' ? '#10b98108' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ fontWeight: '700', marginBottom: '0.5rem', color: formData.setup_type === 'single' ? 'var(--primary)' : 'var(--text)' }}>Single Company</div>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Best for independent companies managing their own CSR projects.</p>
                  </div>
                  <div
                    onClick={() => setFormData(prev => ({ ...prev, setup_type: 'group' }))}
                    style={{
                      padding: '1.5rem',
                      border: `2px solid ${formData.setup_type === 'group' ? 'var(--primary)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: formData.setup_type === 'group' ? '#10b98108' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ fontWeight: '700', marginBottom: '0.5rem', color: formData.setup_type === 'group' ? 'var(--primary)' : 'var(--text)' }}>Group of Companies</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>You are a primary account managing multiple companies/units</div>
                  </div>
                </div>
              </div>



              <div className="form-row">
                <div className="form-group">
                  <label>CIN (Corporate Identity Number) *</label>
                  <input
                    type="text"
                    name="cin"
                    value={formData.cin}
                    onChange={handleChange}
                    required
                    placeholder="U12345AB1234PLC123456"
                  />
                </div>

                <div className="form-group">
                  <label>PAN (Permanent Account Number) *</label>
                  <input
                    type="text"
                    name="pan"
                    value={formData.pan}
                    onChange={handleChange}
                    required
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                </div>
              </div>

              <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Company Address</h2>

              <div className="form-group">
                <label>Street Address *</label>
                <input
                  type="text"
                  name="address.street"
                  value={formData.address.street}
                  onChange={handleChange}
                  required
                  placeholder="Building, Street"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>State *</label>
                  <input
                    type="text"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="address.pincode"
                    value={formData.address.pincode}
                    onChange={handleChange}
                    required
                    pattern="[0-9]{6}"
                    maxLength={6}
                  />
                </div>

                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="address.country"
                    value={formData.address.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <h2 style={{ marginTop: '2rem', marginBottom: '1rem' }}>CSR Contact Information</h2>

              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="csr_contact.name"
                  value={formData.csr_contact.name}
                  onChange={handleChange}
                  required
                  placeholder="Full name"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="csr_contact.email"
                    value={formData.csr_contact.email}
                    onChange={handleChange}
                    required
                    placeholder="contact@company.com"
                  />
                </div>

                <div className="form-group">
                  <label>Phone *</label>
                  <input
                    type="tel"
                    name="csr_contact.phone"
                    value={formData.csr_contact.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>


              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyFirstRun;